import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
  orderBy,
} from "firebase/firestore";

export default function DealerDetails() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const [propertyId, setPropertyId] = useState("");
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    address: "",
  });

  const [editId, setEditId] = useState("");

  /* ================= AUTH + PROPERTY ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (!roleSnap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }
      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH DEALERS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "dealers"),
      where("propertyId", "==", propertyId),
      orderBy("created", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      setDealers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= ADD ================= */
  const handleAdd = async () => {
    if (!form.name || form.mobile.length !== 10) {
      Toast.fire({ icon: "error", title: "Invalid input" });
      return;
    }

    await addDoc(collection(db, "dealers"), {
      ...form,
      propertyId,
      created: Timestamp.now(),
    });

    setForm({ name: "", mobile: "", address: "" });
    setOpenAdd(false);

    Toast.fire({ icon: "success", title: "Dealer Added" });
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    await updateDoc(doc(db, "dealers", editId), {
      ...form,
      updatedAt: Timestamp.now(),
    });

    setOpenEdit(false);
    Toast.fire({ icon: "success", title: "Dealer Updated" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Delete Dealer?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!res.isConfirmed) return;

    await deleteDoc(doc(db, "dealers", id));
    Toast.fire({ icon: "success", title: "Dealer Deleted" });
  };

  const filteredDealers = dealers.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box  className=" p-4">
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Dealer Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add Dealer
            </Button>
          </Stack>

          <TextField
            size="small"
            label="Search Dealer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sr</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDealers.map((row, i) => (
                  <TableRow key={row.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.mobile}</TableCell>
                    <TableCell>{row.address}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditId(row.id);
                          setForm({
                            name: row.name,
                            mobile: row.mobile,
                            address: row.address,
                          });
                          setOpenEdit(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        color="error"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ADD / EDIT DIALOG */}
      <Dialog
        open={openAdd || openEdit}
        onClose={() => {
          setOpenAdd(false);
          setOpenEdit(false);
        }}
      >
        <DialogTitle>{openAdd ? "Add Dealer" : "Edit Dealer"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Dealer Name"
            margin="dense"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Mobile"
            margin="dense"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          />
          <TextField
            fullWidth
            label="Address"
            margin="dense"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAdd(false); setOpenEdit(false); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={openAdd ? handleAdd : handleUpdate}>
            {openAdd ? "Save" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
